import _ from 'underscore';
import _s from 'underscore.string';
import moment from 'moment';
import qs from 'qs';
import { dateFormats } from './constants';

const { ISO_8601 } = dateFormats;
const NOT_LETTER = /[^a-zA-Z]/g;

function capitalize(rawName) {
  let name = _s.capitalize(rawName);

  // do a better job of printing McDonald, McCoy, etc.
  if (name.indexOf('Mc') > -1) {
    const mcIndex = name.indexOf('Mc');
    name = `${name[ mcIndex ]}${name[ mcIndex + 1 ]}${_s.capitalize(name.slice(mcIndex + 2))}`;
  }

  // capitalize hyphenated names appropriately
  if (name.indexOf('-') > -1) {
    name = name.split('-').map(pc => (pc.length ? `${pc[ 0 ].toUpperCase()}${pc.slice(1)}` : '')).join('-');
  }

  // capitalize names appropriately that contain apostrophes
  if (name.indexOf('\'') > -1) {
    name = name.split('\'').map(pc => (pc.length ? `${pc[ 0 ].toUpperCase()}${pc.slice(1)}` : '')).join('\'');
  }

  return name;
}

export function capitalizeEachWord(string) {
  if (typeof string !== 'string') {
    return '';
  }

  return _.chain(string.toLowerCase().split(' '))
    .filter(word => word.length > 0)
    .map(capitalize)
    .flatten()
    .value()
    .join(' ');
}

export function fuzzyFind(list, search) {
  if (typeof search !== 'string') {
    throw new TypeError('search must be a string');
  }

  search = search.toUpperCase().replace(NOT_LETTER, '');

  const filtered = _.filter(list, (item) => {
    if (typeof item !== 'string') {
      return false;
    }

    return item.toUpperCase().replace(NOT_LETTER, '').indexOf(search) !== -1;
  });

  if (filtered.length === 1) {
    return filtered[ 0 ];
  }

  return null;
}

export function generateAcronym(tourneyName) {
  const words = tourneyName.split(' ');

  if (words.length > 1) {
    return _.map(words, word => word[ 0 ]).join('').toUpperCase();
  } else {
    return tourneyName.slice(0, 3).toUpperCase();
  }
}

export function getDateRange({ minDate, maxDate, format = ISO_8601 }) {
  const dates = [];

  let min = moment(minDate, format);
  let max = moment(maxDate, format);

  if (min.isValid() && max.isValid()) {
    while (!min.isAfter(max)) {
      dates.push(min.format(format));
      min = min.add(1, 'd');
    }

    return dates;
  } else {
    return dates;
  }
}

export function getEndOfDayTimestamp(date, format = dateFormats.ISO_8601) {
  const oneDayMilliseconds = (1000 * 60 * 60 * 24);
  return moment(date, format).valueOf() + oneDayMilliseconds - 1;
}

export function getInitials(name) {
  if (typeof name !== 'string') {
    return '';
  }

  let output = `${name.toUpperCase()}`;
  let pcs = output.split(' ');
  let joiner = '';
  if (pcs.length === 1) {
    pcs = output.split('@');
    joiner = '@';
  }

  return _.map(pcs, ((p) => {
    if (p.length > 0) {
      return p[ 0 ];
    } else {
      return '';
    }
  })).join(joiner);
}

export function getPageParams({ pageNo, pageSize }) {
  pageNo = typeof pageNo === 'string' ? +pageNo : pageNo;
  pageSize = typeof pageSize === 'string' ? +pageSize : pageSize;

  if (isNaN(pageNo) || typeof pageNo !== 'number' ||
    isNaN(pageSize) || typeof pageSize !== 'number' ||
    pageNo < 0 || pageSize < 0) {
    return null;
  }

  return {
    start: (pageNo * pageSize),
    count: pageSize
  };
}

export function getSortParams({ attribute, desc }) {
  return (typeof attribute === 'string' && attribute.trim().length > 0 && _.contains(
    [ 'string', 'boolean' ], typeof desc)) ? { sort: `${desc && desc !== 'false' ? 'D' : 'A'}|${attribute}` } : null;
}

export function isValidFromDate(fromDate, toDate) {
  if (fromDate && toDate) {
    let momentFromDate = moment(fromDate, dateFormats.ISO_8601);
    let momentToDate = moment(toDate, dateFormats.ISO_8601);
    let isSame = momentFromDate.isSame(momentToDate, 'days');
    let isBefore = momentFromDate.isBefore(momentToDate, 'days');

    return (isSame || isBefore);
  } else {
    return true;
  }
}

export function isTextEmpty(input) {
  return input ? (input.replace(/^\s+|\s+$/gm, '').length === 0) : true;
}

export function isValidToDate(toDate, fromDate) {
  if (fromDate && toDate) {
    let momentToDate = moment(toDate, dateFormats.ISO_8601);
    let momentFromDate = moment(fromDate, dateFormats.ISO_8601);
    let isSame = momentToDate.isSame(momentFromDate, 'days');
    let isAfter = momentToDate.isAfter(momentFromDate, 'days');

    return isSame || isAfter;
  } else {
    return true;
  }
}

// this takes an object and replaces the existing object that matches on the key
export function replaceOrInsert(array, object, { key = 'id', replaceOnly = false } = {}) {
  if (!_.isArray(array) || array.length === 0) {
    return [ object ];
  }

  const old = _.find(array, (item) => item[ key ] === object[ key ]);
  if (!old) {
    if (replaceOnly) {
      return array;
    }

    return array.concat([ object ]);
  }

  const indexOfOld = _.indexOf(array, old);
  const newArray = _.clone(array);

  newArray.splice(indexOfOld, 1, object);
  return newArray;
}

/**
 * Returns true if the value when paramified will result in an empty query value
 * @param value to check
 * @returns {*|boolean} true if results in an empty value
 */
function isEmptyQueryValue(value) {
  return (_.isArray(value) && value.length === 0) ||
    (_.isString(value) && value.length === 0) ||
    _.isNull(value) ||
    _.isUndefined(value);
}

export function paramify(paramObject) {
  return qs.stringify(
    _.omit(
      paramObject,
      value => isEmptyQueryValue(value)
    ),
    { arrayFormat: 'repeat', delimiter: '&' }
  );
}

export function arrayify(input) {
  if (!input) {
    return [];
  } else if (_.isArray(input)) {
    return input;
  } else {
    return [ input ];
  }
}

export function replace(array, object, options) {
  return replaceOrInsert(array, object, { ...options, replaceOnly: true });
}

export function pluralize(count, word) {
  return count === 1 ? word : `${word}s`;
}

export function arrayMove(array, fromIndex, toIndex) {
  if (!_.isArray(array)) {
    return [];
  }

  if (typeof fromIndex !== 'number' || typeof toIndex !== 'number' ||
    fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
    return array;
  }

  array = array.concat([]);

  array.splice(fromIndex < toIndex ? toIndex - 1 : toIndex, 0, array.splice(fromIndex, 1)[ 0 ]);

  return array;
}

export const getTableSortProps = ({ attribute, querySort, handler }) => {
  if (_.isEmpty(attribute) || !handler) {
    return { onSort: null };
  } else if (_.isEmpty(querySort)) {
    return { onSort: () => handler(`A|${attribute}`) };
  } else {
    const sortInfo = querySort.split('|');
    const ascending = sortInfo[ 0 ] === 'A';
    const sortedAttr = sortInfo[ 1 ];
    let sorted;
    let nextSort = 'A';

    if (sortedAttr === attribute) {
      nextSort = ascending ? 'D' : 'A';
    }

    if (attribute === sortedAttr) {
      sorted = sortInfo[ 0 ];
    }

    return { sorted, onSort: () => handler(`${nextSort}|${attribute}`) };
  }
};


// WEBPACK FOOTER //
// ./src/js/util/Etc.js